import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2, Lightbulb } from "lucide-react";

interface RefinePanelProps {
  /** Loading state while regeneration runs */
  isLoading: boolean;
  /** Called with the additional context text when the user clicks regenerate */
  onRegenerate: (additionalContext: string) => void | Promise<void>;
  /** Optional title override */
  title?: string;
  /** Optional helper copy */
  description?: string;
  /** Optional placeholder for the textarea */
  placeholder?: string;
  /** Persist-across-renders initial value (e.g. last submitted refinement) */
  initialValue?: string;
}

const DEFAULT_PLACEHOLDER =
  "Examples:\n• The site also has a loading dock on the north side that's unstaffed after 8pm\n• Two recent break-in attempts at the adjacent property in the last 60 days\n• Client wants armed coverage during cash deposits on Fridays\n• Consider weather-related access changes during winter months";

const RefinePanel = ({
  isLoading,
  onRegenerate,
  title = "Add More Details & Regenerate",
  description = "Did the AI mention something you hadn't considered? Add any missing context, corrections, or new requirements below — then regenerate to get an improved version.",
  placeholder = DEFAULT_PLACEHOLDER,
  initialValue = "",
}: RefinePanelProps) => {
  const [text, setText] = useState(initialValue);

  const handleClick = () => {
    const trimmed = text.trim();
    if (!trimmed || isLoading) return;
    onRegenerate(trimmed);
  };

  return (
    <Card className="border-primary/30 bg-card shadow-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-foreground text-base">
          <Lightbulb className="h-5 w-5 text-primary" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{description}</p>
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="min-h-[140px] bg-secondary border-border text-foreground"
          disabled={isLoading}
        />
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-xs text-muted-foreground">
            Regenerating replaces the current AI output with a refined version that incorporates these details.
          </p>
          <Button
            onClick={handleClick}
            disabled={isLoading || !text.trim()}
            className="gradient-primary text-primary-foreground shadow-glow hover:opacity-90"
          >
            {isLoading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Regenerating...</>
            ) : (
              <><Sparkles className="mr-2 h-4 w-4" /> Regenerate with These Details</>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default RefinePanel;
